/**
 * Audio graph setup: mic capture, worklets, and track playback.
 *
 * Every timestamp that leaves this module is in AudioContext time. Nothing
 * here reads Date.now() or requestAnimationFrame — those are wall-clock and
 * frame-clock respectively, and neither is locked to the audio hardware that
 * actually decides when a sound happened.
 */

import type { ScoringConfig } from "@catchup/shared";

export interface DetectedOnset {
  /** AudioContext time, uncorrected for latency. */
  time: number;
  strength: number;
}

export interface AudioEngine {
  ctx: AudioContext;
  stream: MediaStream;
  onset: AudioWorkletNode;
  capture: AudioWorkletNode;
  inputLabel: string;
  destroy: () => Promise<void>;
}

export async function createEngine(config: ScoringConfig): Promise<AudioEngine> {
  const stream = await navigator.mediaDevices.getUserMedia({
    audio: {
      // All three of these must be off. They are designed to make speech
      // intelligible on a call, and they do it by gating, gain-riding, and
      // time-shifting the signal — which is precisely the information we are
      // trying to measure. Echo cancellation in particular will happily
      // suppress the onsets we need.
      echoCancellation: false,
      noiseSuppression: false,
      autoGainControl: false,
      channelCount: 1,
    },
  });

  const ctx = new AudioContext({ latencyHint: "interactive" });
  await ctx.resume();

  const base = import.meta.env.BASE_URL || "/";
  await ctx.audioWorklet.addModule(`${base}worklets/onset-processor.js`);
  await ctx.audioWorklet.addModule(`${base}worklets/capture-processor.js`);

  const source = ctx.createMediaStreamSource(stream);

  const onset = new AudioWorkletNode(ctx, "onset-processor", {
    numberOfInputs: 1,
    numberOfOutputs: 0,
    processorOptions: { config: config.onset },
  });
  const capture = new AudioWorkletNode(ctx, "capture-processor", {
    numberOfInputs: 1,
    numberOfOutputs: 0,
  });

  source.connect(onset);
  source.connect(capture);

  const track = stream.getAudioTracks()[0];

  return {
    ctx,
    stream,
    onset,
    capture,
    inputLabel: track?.label ?? "unknown input",
    destroy: async () => {
      onset.disconnect();
      capture.disconnect();
      source.disconnect();
      for (const t of stream.getTracks()) t.stop();
      await ctx.close();
    },
  };
}

/**
 * Collects onsets from the worklet between arm and disarm.
 * Returns a disposer plus the accumulated onsets.
 */
export function collectOnsets(
  node: AudioWorkletNode,
  onLevel?: (db: number) => void,
): { onsets: DetectedOnset[]; arm: () => Promise<number>; stop: () => DetectedOnset[] } {
  const onsets: DetectedOnset[] = [];

  const handler = (e: MessageEvent) => {
    const msg = e.data;
    if (msg?.type === "onset") {
      onsets.push({ time: msg.time, strength: msg.strength });
    } else if (msg?.type === "level" && onLevel) {
      onLevel(msg.db);
    }
  };
  node.port.addEventListener("message", handler);
  node.port.start?.();

  return {
    onsets,
    /** Resolves with the context time at which the worklet actually armed. */
    arm: () =>
      new Promise<number>((resolve) => {
        const once = (e: MessageEvent) => {
          if (e.data?.type === "armed") {
            node.port.removeEventListener("message", once);
            resolve(e.data.contextTime as number);
          }
        };
        node.port.addEventListener("message", once);
        onsets.length = 0;
        node.port.postMessage({ type: "arm" });
      }),
    stop: () => {
      node.port.postMessage({ type: "disarm" });
      node.port.removeEventListener("message", handler);
      return onsets;
    },
  };
}

/**
 * Decode a user-picked file into an AudioBuffer.
 *
 * The file never leaves the browser: it is read with FileReader and decoded in
 * process. No audio is uploaded, and there is no server route that would
 * accept it if we tried.
 */
export async function decodeFile(ctx: AudioContext, file: File): Promise<AudioBuffer> {
  const bytes = await file.arrayBuffer();
  return await ctx.decodeAudioData(bytes);
}

export interface PlaybackHandle {
  /** Context time at which `from` in the track was heard. */
  startedAt: number;
  stop: () => void;
}

/** Play a slice of the track. `onEnded` fires when the slice finishes. */
export function playSlice(
  ctx: AudioContext,
  buffer: AudioBuffer,
  from: number,
  to: number,
  onEnded?: () => void,
): PlaybackHandle {
  const src = ctx.createBufferSource();
  src.buffer = buffer;
  src.connect(ctx.destination);
  // Schedule slightly ahead so the start time is exact rather than "whenever
  // the thread got round to it".
  const startAt = ctx.currentTime + 0.08;
  src.start(startAt, from, Math.max(0.05, to - from));
  if (onEnded) src.onended = onEnded;
  return {
    startedAt: startAt,
    stop: () => {
      try {
        src.onended = null;
        src.stop();
      } catch {
        /* already stopped */
      }
    },
  };
}
